
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { ServiceVisit } from '@/types';
import ServiceHistoryCard from '@/components/dashboard/ServiceHistoryCard';
import { PlantRange, ServiceType } from '@/types';

const ServiceHistory: React.FC = () => {
  // Mock data - replace with API call in production
  const MOCK_SERVICE_VISITS = [
    {
      id: "visit3",
      userId: "user1",
      technicianId: "tech1",
      technicianName: "John Smith",
      date: new Date(2025, 3, 15),
      status: 'completed' as const,
      plantCount: PlantRange.Range2,
      actualPlantCount: 15,
      checkInTime: new Date(2025, 3, 15, 10, 0),
      checkOutTime: new Date(2025, 3, 15, 10, 45),
      duration: 45,
      notes: "All plants watered and healthy. Recommended more light for the ficus.",
      issues: [
        { type: 'pest' as const, description: 'Spider mites on the monstera', severity: 'medium' as const },
        { type: 'under-watering' as const, description: 'Peace lily was very dry', severity: 'high' as const }
      ],
      serviceType: ServiceType.Indoor,
    },
    {
      id: "visit4",
      userId: "user1",
      technicianId: "tech2",
      technicianName: "Jane Doe",
      date: new Date(2025, 3, 1),
      status: 'completed' as const,
      plantCount: PlantRange.Range2,
      actualPlantCount: 12,
      checkInTime: new Date(2025, 3, 1, 14, 0),
      checkOutTime: new Date(2025, 3, 1, 14, 30),
      duration: 30,
      notes: "All plants watered. Added soil to two pots.",
      issues: [],
      serviceType: ServiceType.Combined,
    },
    {
      id: "visit5",
      userId: "user1",
      technicianId: "tech1",
      technicianName: "John Smith",
      date: new Date(2025, 2, 15),
      status: 'completed' as const,
      plantCount: PlantRange.Range2,
      actualPlantCount: 14,
      checkInTime: new Date(2025, 2, 15, 9, 0),
      checkOutTime: new Date(2025, 2, 15, 9, 45),
      duration: 45,
      notes: "All plants looking healthy. Pruned the monstera.",
      issues: [],
      serviceType: ServiceType.Indoor,
    }
  ];

  const downloadServiceReport = (visitId: string) => {
    // In a real app, this would generate a PDF report
    console.log(`Downloading report for visit ${visitId}`);
    alert(`Report for visit ${visitId} would be downloaded here`);
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container max-w-4xl mx-auto px-4">
        <Card className="border-front-gray-200 bg-white shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-bold text-front-gray-900">Service History</CardTitle>
            <CardDescription className="text-front-gray-600">
              Complete record of your plant care visits
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="overflow-hidden rounded-lg border border-front-gray-200">
              <Table>
                <TableHeader className="bg-front-gray-50">
                  <TableRow className="border-front-gray-200">
                    <TableHead className="font-semibold text-front-gray-900">Date</TableHead>
                    <TableHead className="font-semibold text-front-gray-900">Technician</TableHead>
                    <TableHead className="font-semibold text-front-gray-900">Service Type</TableHead>
                    <TableHead className="font-semibold text-front-gray-900">Duration</TableHead>
                    <TableHead className="text-right font-semibold text-front-gray-900">Report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {MOCK_SERVICE_VISITS.map((visit) => (
                    <TableRow key={visit.id} className="border-front-gray-200 hover:bg-front-gray-50">
                      <TableCell className="text-front-gray-900">
                        {format(visit.date, 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-front-gray-900">
                        {visit.technicianName}
                      </TableCell>
                      <TableCell className="text-front-gray-900">
                        {visit.serviceType}
                      </TableCell>
                      <TableCell className="text-front-gray-900">
                        {visit.duration} mins
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadServiceReport(visit.id)}
                          className="text-front-gray-600 hover:text-front-gray-900 hover:bg-front-gray-100"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Report
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-front-gray-900">Detailed Service Records</h3>
              <ScrollArea className="h-[400px] rounded-lg border border-front-gray-200 bg-white">
                <div className="p-4 space-y-4">
                  {MOCK_SERVICE_VISITS.map((visit) => (
                    <ServiceHistoryCard key={visit.id} visit={visit} />
                  ))}
                </div>
              </ScrollArea>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ServiceHistory;
